import type { TFunction } from "i18next";
import type {
	ElectricityMeterRow,
	HeatMeterRow,
	WaterMeterRow,
} from "@/api/districts/types";
import { MeterRowActions } from "@/components/meters/meter-row-actions";
import type { Column } from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { EmptyCell } from "@/components/ui/empty-cell";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { displayDate } from "@/lib/empty-value";

function renderDecimal(value: number | null | undefined) {
	if (value === null || value === undefined || Number.isNaN(value))
		return <EmptyCell />;
	return value.toFixed(3);
}

function getMonthNames(locale: string) {
	const now = new Date();
	const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
	const fmt = new Intl.DateTimeFormat(locale, { month: "long" });
	return {
		current: fmt.format(now),
		previous: fmt.format(previous),
	};
}

export function getWaterColumns(
	t: TFunction,
	locale: string,
): Column<WaterMeterRow>[] {
	const { current, previous } = getMonthNames(locale);
	return [
		{
			key: "type",
			label: t("meters.colType"),
			sortField: "type",
			render: (row) => (
				<Tooltip>
					<TooltipTrigger asChild>
						<span
							className={`inline-block size-3 rounded-full ${row.type === "hot" ? "bg-red-500" : "bg-blue-500"}`}
							aria-label={
								row.type === "hot"
									? t("meters.hotWater")
									: t("meters.coldWater")
							}
						/>
					</TooltipTrigger>
					<TooltipContent>
						{row.type === "hot" ? t("meters.hotWater") : t("meters.coldWater")}
					</TooltipContent>
				</Tooltip>
			),
		},
		{
			key: "serial_number",
			label: t("meters.colSerialNumber"),
			sortField: "serial_number",
			render: (row) => (
				<CopyableCell
					value={row.serial_number}
					label={t("meters.colSerialNumber")}
				/>
			),
		},
		{
			key: "last_reading_value",
			label: t("meters.colValueWater"),
			sortField: "last_reading_value",
			render: (row) => renderDecimal(row.last_reading_value),
		},
		{
			key: "last_month_delta",
			label: t("meters.colLastMonthWater", { month: previous }),
			sortField: "last_month_delta",
			render: (row) => renderDecimal(row.last_month_delta),
		},
		{
			key: "current_month_delta",
			label: t("meters.colCurrentMonthWater", { month: current }),
			sortField: "current_month_delta",
			render: (row) => renderDecimal(row.current_month_delta),
		},
		{
			key: "last_reading_date",
			label: t("meters.colLastReading"),
			sortField: "last_reading_date",
			render: (row) => displayDate(row.last_reading_date, locale),
		},
		{
			key: "last_alarm",
			label: t("meters.colLastAlarm"),
			sortField: "last_alarm",
			render: (row) =>
				row.last_alarm ? (
					<span className="text-red-600">{row.last_alarm}</span>
				) : (
					<EmptyCell className="text-green-600" />
				),
		},
		{
			key: "address",
			label: t("meters.colAddress"),
			sortField: "address",
			render: (row) => row.address ?? <EmptyCell />,
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<MeterRowActions
					meterType="woda"
					serialNumber={row.serial_number}
					isAssigned={!!row.address}
				/>
			),
		},
	];
}

export function getHeatColumns(
	t: TFunction,
	locale: string,
): Column<HeatMeterRow>[] {
	const { current, previous } = getMonthNames(locale);
	return [
		{
			key: "serial_number",
			label: t("meters.colSerialNumber"),
			sortField: "serial_number",
			render: (row) => (
				<CopyableCell
					value={row.serial_number}
					label={t("meters.colSerialNumber")}
				/>
			),
		},
		{
			key: "last_reading_value",
			label: t("meters.colValueHeat"),
			sortField: "last_reading_value",
			render: (row) => renderDecimal(row.last_reading_value),
		},
		{
			key: "last_month_delta",
			label: t("meters.colLastMonthHeat", { month: previous }),
			sortField: "last_month_delta",
			render: (row) => renderDecimal(row.last_month_delta),
		},
		{
			key: "current_month_delta",
			label: t("meters.colCurrentMonthHeat", { month: current }),
			sortField: "current_month_delta",
			render: (row) => renderDecimal(row.current_month_delta),
		},
		{
			key: "last_reading_date",
			label: t("meters.colLastReading"),
			sortField: "last_reading_date",
			render: (row) => displayDate(row.last_reading_date, locale),
		},
		{
			key: "last_alarm",
			label: t("meters.colLastAlarm"),
			sortField: "last_alarm",
			render: (row) =>
				row.last_alarm ? (
					<span className="text-red-600">{row.last_alarm}</span>
				) : (
					<EmptyCell className="text-green-600" />
				),
		},
		{
			key: "address",
			label: t("meters.colAddress"),
			sortField: "address",
			render: (row) => row.address ?? <EmptyCell />,
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<MeterRowActions
					meterType="cieplo"
					serialNumber={row.serial_number}
					isAssigned={!!row.address}
				/>
			),
		},
	];
}

export function getElectricityColumns(
	t: TFunction,
	locale: string,
): Column<ElectricityMeterRow>[] {
	const { current, previous } = getMonthNames(locale);
	return [
		{
			key: "serial_number",
			label: t("meters.colSerialNumber"),
			sortField: "serial_number",
			render: (row) => (
				<CopyableCell
					value={row.serial_number}
					label={t("meters.colSerialNumber")}
				/>
			),
		},
		{
			key: "last_reading_value",
			label: t("meters.colValueElectricity"),
			sortField: "last_reading_value",
			render: (row) => renderDecimal(row.last_reading_value),
		},
		{
			key: "last_month_delta",
			label: t("meters.colLastMonthElectricity", { month: previous }),
			sortField: "last_month_delta",
			render: (row) => renderDecimal(row.last_month_delta),
		},
		{
			key: "current_month_delta",
			label: t("meters.colCurrentMonthElectricity", { month: current }),
			sortField: "current_month_delta",
			render: (row) => renderDecimal(row.current_month_delta),
		},
		{
			key: "last_reading_date",
			label: t("meters.colLastReading"),
			sortField: "last_reading_date",
			render: (row) => displayDate(row.last_reading_date, locale),
		},
		{
			key: "address",
			label: t("meters.colAddress"),
			sortField: "address",
			render: (row) => row.address ?? <EmptyCell />,
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<MeterRowActions
					meterType="energia"
					serialNumber={row.serial_number}
					isAssigned={!!row.address}
				/>
			),
		},
	];
}
